export interface ChecklistProps {
  id: number;
  title: string;
  role: string;
  checklist_type?:number;
  type?: number;
  application:
    'pre-shoot' | 'post-shoot' | 'mid-shoot' | 'post-production' | 'final-cut';
  tasks: Task[];
  lastUpdate: {
    updatedBy: string;
    date: string;
  };
  active: boolean;
  usage: number;
  compliance: number;
}

export interface Task {
  title: string;
  priority: 'critical' | 'important' | 'optional';
  inputType: 'checkbox' | 'text-input' | 'file-upload';
  completion: number;
}

export interface ChecklistUsageProps {
  id: number;
  event: {
    name: string;
    type: string;  
  };
  pilot: {
    id: string;
    name: string;
    role: string;
  };
  checklist_title: string;
  application:
    'pre-shoot' | 'post-shoot' | 'mid-shoot' | 'post-production' | 'final-cut';
  tasks: number;
  tasks_completed: number;
  status: string;
  completion: number;
  date: string;
}

export const checklists: ChecklistProps[] = [
  {
    id: 1,
    title: "Camera Equipment Verification",
    role: "Videographer",
    application: "pre-shoot",
    lastUpdate: {
      updatedBy: "MZKP001 - Tendai Moyo",
      date: "2025-11-08"
    },
    active: false,
    usage: 247,
    compliance: 56,
    tasks: [
      {
        title: "Verify all camera batteries are fully charged",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Check lens condition and clean if necessary",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Test camera autofocus and manual focus",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Confirm SD card storage capacity and format",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Pack backup camera body",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Note any equipment issues or concerns",
        priority: "optional",
        inputType: "text-input",
        completion: 80
      }
    ]
  },
  {
    id: 2,
    title: "Audio Levels & Sync Check",
    role: "Sound Engineer",
    application: "pre-shoot",
    lastUpdate: {
      updatedBy: "MZKP006 - Simba Zvobgo",
      date: "2025-11-09"
    },
    active: true,
    usage: 189,
    compliance: 74,
    tasks: [
      {
        title: "Test all microphones for proper signal",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Verify audio recorder battery levels",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Conduct sound check at venue location",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Sync audio timecode with camera",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Test backup wireless lav mics",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Upload audio test file",
        priority: "optional",
        inputType: "file-upload",
        completion: 90
      }
    ]
  },
  {
    id: 3,
    title: "Footage Transfer & Backup",
    role: "Videographer",
    application: "post-shoot",
    lastUpdate: {
      updatedBy: "MZKP003 - Tatenda Maposa",
      date: "2025-11-07"
    },
    active: false,
    usage: 312,
    compliance: 34,
    tasks: [
      {
        title: "Transfer all footage to primary storage",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Create backup copy on external drive",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Verify file integrity and playback",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Label and organize footage by scene/time",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Format memory cards after verification",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Document total footage size and duration",
        priority: "optional",
        inputType: "text-input",
        completion: 65
      }
    ]
  },
  {
    id: 4,
    title: "Color Grading Workflow",
    role: "Video Editor",
    application: "post-production",
    lastUpdate: {
      updatedBy: "MZKP009 - Tafadzwa Chimedza",
      date: "2025-11-06"
    },
    active: true,
    usage: 156,
    compliance: 66,
    tasks: [
      {
        title: "Apply color correction to all clips",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Match color temperature across scenes",
        priority: "critical",
        inputType: "checkbox",
        completion: 85
      },
      {
        title: "Create and apply LUT for brand consistency",
        priority: "important",
        inputType: "checkbox",
        completion: 90
      },
      {
        title: "Adjust skin tones and highlights",
        priority: "important",
        inputType: "checkbox",
        completion: 80
      },
      {
        title: "Upload before/after comparison samples",
        priority: "optional",
        inputType: "file-upload",
        completion: 70
      }
    ]
  },
  {
    id: 5,
    title: "Client Revision Notes",
    role: "Producer",
    application: "final-cut",
    lastUpdate: {
      updatedBy: "MZKP011 - Vimbai Shereni",
      date: "2025-11-10"
    },
    active: true,
    usage: 203,
    compliance: 96,
    tasks: [
      {
        title: "Review all client feedback points",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Implement requested edits and cuts",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Verify music and graphics changes",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Confirm timing adjustments",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Document changes made in revision log",
        priority: "optional",
        inputType: "text-input",
        completion: 95
      },
      {
        title: "Upload revised version for client review",
        priority: "critical",
        inputType: "file-upload",
        completion: 100
      }
    ]
  },
  {
    id: 6,
    title: "Lighting Setup & White Balance",
    role: "Photographer",
    application: "pre-shoot",
    lastUpdate: {
      updatedBy: "MZKP007 - Brian Dube",
      date: "2025-11-05"
    },
    active: false,
    usage: 278,
    compliance: 78,
    tasks: [
      {
        title: "Set up key, fill, and back lights",
        priority: "critical",
        inputType: "checkbox",
        completion: 75
      },
      {
        title: "Perform white balance calibration",
        priority: "critical",
        inputType: "checkbox",
        completion: 80
      },
      {
        title: "Test lighting ratios and shadows",
        priority: "important",
        inputType: "checkbox",
        completion: 70
      },
      {
        title: "Adjust for ambient light conditions",
        priority: "important",
        inputType: "checkbox",
        completion: 85
      },
      {
        title: "Take test shots and review exposure",
        priority: "important",
        inputType: "checkbox",
        completion: 90
      },
      {
        title: "Note lighting setup configuration",
        priority: "optional",
        inputType: "text-input",
        completion: 60
      }
    ]
  },
  {
    id: 7,
    title: "Audio Mix & Mastering",
    role: "Sound Engineer",
    application: "post-production",
    lastUpdate: {
      updatedBy: "MZKP014 - Blessing Marimo",
      date: "2025-11-04"
    },
    active: true,
    usage: 134,
    compliance: 24,
    tasks: [
      {
        title: "Balance dialogue and ambient audio levels",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Remove background noise and hiss",
        priority: "critical",
        inputType: "checkbox",
        completion: 95
      },
      {
        title: "Add music and sound effects",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Apply compression and EQ adjustments",
        priority: "important",
        inputType: "checkbox",
        completion: 85
      },
      {
        title: "Master final audio to broadcast standards",
        priority: "critical",
        inputType: "checkbox",
        completion: 90
      },
      {
        title: "Export audio stems for backup",
        priority: "optional",
        inputType: "checkbox",
        completion: 70
      }
    ]
  },
  {
    id: 8,
    title: "Export Settings & Deliverables",
    role: "Video Editor",
    application: "final-cut",
    lastUpdate: {
      updatedBy: "MZKP013 - Tariro Gonese",
      date: "2025-11-09"
    },
    active: true,
    usage: 421,
    compliance: 99,
    tasks: [
      {
        title: "Verify export resolution and framerate",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Set correct codec and bitrate",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Export master file in highest quality",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Create web-optimized versions",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Generate thumbnails and preview files",
        priority: "important",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Upload all deliverables to client portal",
        priority: "critical",
        inputType: "file-upload",
        completion: 100
      },
      {
        title: "Confirm file naming conventions",
        priority: "optional",
        inputType: "text-input",
        completion: 95
      }
    ]
  },
  {
    id: 9,
    title: "Scene Continuity Log",
    role: "Videographer",
    application: "post-shoot",
    lastUpdate: {
      updatedBy: "MZKP018 - Takudzwa Rusike",
      date: "2025-11-03"
    },
    active: true,
    usage: 167,
    compliance: 71,
    tasks: [
      {
        title: "Log all scene numbers and takes",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Note timestamp for key moments",
        priority: "important",
        inputType: "checkbox",
        completion: 85
      },
      {
        title: "Document any technical issues during shoot",
        priority: "important",
        inputType: "text-input",
        completion: 70
      },
      {
        title: "Mark best takes for editing review",
        priority: "important",
        inputType: "checkbox",
        completion: 90
      },
      {
        title: "Cross-reference with shot list",
        priority: "optional",
        inputType: "checkbox",
        completion: 65
      },
      {
        title: "Upload continuity notes document",
        priority: "optional",
        inputType: "file-upload",
        completion: 50
      }
    ]
  },
  {
    id: 10,
    title: "Streaming Platform Configuration",
    role: "Producer",
    application: "pre-shoot",
    lastUpdate: {
      updatedBy: "MZKP005 - Chipo Mutasa",
      date: "2025-11-08"
    },
    active: false,
    usage: 295,
    compliance: 59,
    tasks: [
      {
        title: "Configure stream key and server settings",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Set streaming bitrate and resolution",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Test internet connection speed and stability",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Configure chat moderation and settings",
        priority: "important",
        inputType: "checkbox",
        completion: 95
      },
      {
        title: "Set up stream graphics and overlays",
        priority: "important",
        inputType: "checkbox",
        completion: 90
      },
      {
        title: "Conduct test stream before event",
        priority: "critical",
        inputType: "checkbox",
        completion: 100
      },
      {
        title: "Document platform credentials and backup plan",
        priority: "optional",
        inputType: "text-input",
        completion: 80
      }
    ]
  }
];

export const usage: ChecklistUsageProps[] = [
  {
    id: 1,
    event: {
      name: "Johnson-Smith Wedding",
      type: "Wedding"
    },
    pilot: {
      id: "MZKP001",
      name: "Tendai Moyo",
      role: "Photographer"
    },
    completion: 37,
    date: "2025-11-05",
    checklist_title: "Camera Equipment Verification",
    application: "pre-shoot",
    status: "partial",
    tasks: 6,
    tasks_completed: 2
  },
  {
    id: 2,
    event: {
      name: "Tech Innovate Summit",
      type: "Corporate Event"
    },
    pilot: {
      id: "MZKP002",
      name: "Rudo Ncube",
      role: "Network Engineer"
    },
    completion: 83,
    date: "2025-11-03",
    checklist_title: "Streaming Platform Configuration",
    application: "pre-shoot",
    status: "pending",
    tasks: 7,
    tasks_completed: 6
  },
  {
    id: 3,
    event: {
      name: "Chikwanha Baby Shower",
      type: "Baby Shower"
    },
    pilot: {
      id: "MZKP003",
      name: "Tatenda Maposa",
      role: "Videographer"
    },
    completion: 100,
    date: "2025-10-28",
    checklist_title: "Footage Transfer & Backup",
    application: "post-shoot",
    status: "complete",
    tasks: 6,
    tasks_completed: 6
  },
  {
    id: 4,
    event: {
      name: "Khumalo-Mazivisa Wedding",
      type: "Traditional & White Wedding"
    },
    pilot: {
      id: "MZKP007",
      name: "Brian Dube",
      role: "Producer"
    },
    completion: 53,
    date: "2025-10-10",
    checklist_title: "Lighting Setup & White Balance",
    application: "pre-shoot",
    status: "partial",
    tasks: 6,
    tasks_completed: 3
  },
  {
    id: 5,
    event: {
      name: "Harare International Festival of Arts",
      type: "Festival"
    },
    pilot: {
      id: "MZKP011",
      name: "Vimbai Shereni",
      role: "Sound Engineer"
    },
    completion: 75,
    date: "2025-09-15",
    checklist_title: "Audio Mix & Mastering",
    application: "post-production",
    status: "pending",
    tasks: 6,
    tasks_completed: 5
  }
];
